
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Lock, ArrowLeft, ExternalLink, CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { STRIPE_TEST_MODE } from "@/lib/utils";
import { Capacitor } from '@capacitor/core';
import { openPaymentUrl, saveNativePaymentSession } from '@/utils/nativePayment';
import { useLanguage } from '@/contexts/LanguageContext';


interface PaymentData {
  eventId: number;
  mode: 'individual' | 'parejas';
  partner?: any;
  paymentMode: 'my_part' | 'full_amount';
  amount: number;
  promoCode?: string;
  discountPercent?: number;
}

interface PaymentProcessProps {
  paymentData: PaymentData;
  eventTitle: string;
  eventData?: any;
  registrationData?: any;
  splitPaymentId?: string;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
  onBack: () => void;
}

const translateCategory = (cat: string, language: string): string => {
  if (language === 'es') return cat;
  const map: Record<string, string> = {
    'masculino': "Men's", 'femenino': "Women's", 'mixto': 'Mixed',
  };
  return map[cat.toLowerCase()] || cat;
};

const PaymentProcess = ({
  paymentData, 
  eventTitle, 
  eventData,
  registrationData,
  splitPaymentId,
  onPaymentSuccess,
  onPaymentError, 
  onBack 
}: PaymentProcessProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { t, language } = useLanguage();
  const showMSI = paymentData.amount >= 1500;
  const dateLocale = language === 'es' ? 'es-ES' : 'en-US';

  const handlePayment = async () => {
    setIsProcessing(true);
    
    try {
      let data: any;
      let error: any;

      if (splitPaymentId) {
        // Complete an existing split payment (partner paying their half)
        console.log('Completing split payment for partner...');
        const result = await supabase.functions.invoke('complete-split-payment', {
          body: {
            splitPaymentId,
            eventTitle,
            isNative: Capacitor.isNativePlatform(),
            testMode: STRIPE_TEST_MODE,
            enableInstallments: showMSI,
          }
        });
        data = result.data;
        error = result.error;
      } else {
        const isSplitPayment = registrationData?.paymentMode === 'split';
        const functionName = isSplitPayment ? 'create-split-payment' : 'create-payment';
        console.log(`Creating ${isSplitPayment ? 'split' : 'regular'} payment session...`);

      const result = await supabase.functions.invoke(functionName, {
          body: {
            paymentData,
            eventTitle,
            registrationData: {
              ...registrationData,
              isNative: Capacitor.isNativePlatform(),
            },
            testMode: STRIPE_TEST_MODE,
            enableInstallments: showMSI,
            promoCode: paymentData.promoCode,
            discountPercent: paymentData.discountPercent,
          }
        });
        data = result.data;
        error = result.error;
      }

      if (error) {
        throw new Error(error.message || t('payment.errorCreatingSession'));
      }

      if (!data?.url) {
        throw new Error(t('payment.noPaymentUrl'));
      }

      console.log('Redirecting to Stripe checkout:', data.url);

      if (Capacitor.isNativePlatform()) {
        if (data.sessionId) {
          saveNativePaymentSession(data.sessionId, paymentData.eventId.toString());
        }
        toast.success(t('payment.openingPaymentPage'));

        const { closedByUser } = await openPaymentUrl(data.url);

        if (closedByUser && data.sessionId) {
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('session_id', data.sessionId);
          currentUrl.searchParams.set('payment_pending', 'true');
          currentUrl.searchParams.set('step', '3');
          window.location.href = currentUrl.toString();
          return;
        }
      } else {
        window.location.href = data.url;
      }
      
    } catch (error) {
      console.error("Payment creation failed:", error);
      const errorMessage = error instanceof Error ? error.message : t('payment.errorProcessing');
      toast.error(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const getFormatLabel = (ed: any): string => {
    if (ed.isLeague) {
      if (ed.leagueFormat === 'round-robin') return t('payment.roundRobin');
      if (ed.leagueFormat === 'sumatoria-juegos') return t('payment.sumatoriaJuegos');
      if (ed.leagueFormat === 'escalera-piramidal') return t('payment.escaleraFormat');
      return t('payment.league');
    }
    if (ed.tournamentFormat === 'round-robin') return t('payment.roundRobin');
    if (ed.tournamentFormat === 'eliminacion-directa') return t('payment.directElimination');
    if (ed.tournamentFormat === 'sumatoria-juegos') return t('payment.sumatoriaJuegos');
    return t('payment.tournament');
  };

  return (
    <div className="space-y-6">
      {/* Payment Form - Stripe Checkout Info */}
      <Card className="bg-background/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t('payment.paymentInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <CreditCard className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium text-primary">
                {t('payment.secureStripe')}
              </p>
              <p className="text-xs text-primary/80">
                {t('payment.redirectNotice')}
              </p>
            </div>
          </div>
          
          {/* Security Notice */}
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <Lock className="h-4 w-4 text-green-600" />
            <p className="text-sm text-green-800">
              {t('payment.sslNotice')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* MSI Selector */}
      {showMSI && (
        <Card className="bg-background/80 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-5 w-5" />
              {t('payment.installments')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('payment.msiNote')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Payment Button */}
      <div className="space-y-3">
        <Button 
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {t('payment.creatingSession')}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              {t('payment.proceedPayment')} - ${paymentData.amount.toFixed(2)}
            </div>
          )}
        </Button>
        
        {isProcessing && (
          <p className="text-sm text-center text-muted-foreground">
            {t('payment.openingWindow')}
          </p>
        )}
      </div>

      {/* Terms */}
      <p className="text-xs text-muted-foreground text-center">
        {t('payment.termsNotice')}
      </p>
    </div>
  );
};

export default PaymentProcess;
